import { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import Modal from './Modal';
import { getAccessToken, isShareholderContextUser } from '../authStorage';
import { createShareholderUser, fetchShareholderUsers, updateShareholderUser } from '../shareholderUsersApi';
import { useLanguage } from '../i18n/language';
import './BranchesPage.css';
import './UsersPage.css';
import './DecisionsPage.css';
import './Modal.css';

const emptyForm = () => ({
  userName: '',
  fullName: '',
  email: '',
  phone: '',
  password: '',
  isActive: true,
});

export default function ShareholderUsersPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useMemo(() => ({
    title: isAr ? 'المستخدمين (المساهم)' : 'Users (Shareholder)',
    subtitle: isAr ? 'إدارة مستخدمين تابعين لحساب المساهم الحالي' : 'Manage users under the current shareholder account',
    list: isAr ? 'قائمة' : 'List',
    cards: isAr ? 'بطاقات' : 'Cards',
    addUser: isAr ? 'إضافة مستخدم' : 'Add user',
    editUser: isAr ? 'تعديل' : 'Edit',
    username: isAr ? 'اسم المستخدم' : 'Username',
    fullName: isAr ? 'الاسم الكامل' : 'Full name',
    email: isAr ? 'البريد الإلكتروني' : 'Email',
    phone: isAr ? 'رقم الجوال (اختياري)' : 'Phone (optional)',
    phPhone: isAr ? '05xxxxxxxx أو +966...' : '05xxxxxxxx or +966...',
    password: isAr ? 'كلمة المرور' : 'Password',
    newPasswordHint: isAr ? 'كلمة مرور جديدة (اختياري)' : 'New password (optional)',
    save: isAr ? 'حفظ' : 'Save',
    saveChanges: isAr ? 'حفظ التغييرات' : 'Save changes',
    adding: isAr ? 'جارٍ الإضافة...' : 'Adding...',
    saving: isAr ? 'جارٍ الحفظ...' : 'Saving...',
    cancel: isAr ? 'إلغاء' : 'Cancel',
    modalAddTitle: isAr ? 'إضافة مستخدم تابع' : 'Add managed user',
    modalEditTitle: isAr ? 'تعديل مستخدم' : 'Edit user',
    modalAddSub: isAr ? 'بيانات الحساب الجديد' : 'New account details',
    modalEditSub: isAr ? 'تحديث البيانات والحالة' : 'Update details and status',
    search: isAr ? 'بحث...' : 'Search...',
    loading: isAr ? 'جارٍ التحميل...' : 'Loading...',
    noResults: isAr ? 'لا توجد نتائج' : 'No results',
    active: isAr ? 'نشط' : 'Active',
    inactive: isAr ? 'غير نشط' : 'Inactive',
    colUser: isAr ? 'المستخدم' : 'User',
    colUsername: isAr ? 'اسم المستخدم' : 'Username',
    colStatus: isAr ? 'الحالة' : 'Status',
    colActions: isAr ? 'إجراءات' : 'Actions',
    usernameLabel: isAr ? 'اسم المستخدم:' : 'Username:',
    loadFailed: isAr ? 'فشل التحميل' : 'Failed to load',
    denied: isAr ? 'لا تملك صلاحية عرض هذه الصفحة.' : 'You do not have permission to view this page.',
    userPassRequired: isAr ? 'اسم المستخدم وكلمة المرور مطلوبان' : 'Username and password are required',
    addFailed: isAr ? 'تعذر إضافة المستخدم' : 'Unable to add user',
    updateFailed: isAr ? 'تعذر تحديث المستخدم' : 'Unable to update user',
    activeAccount: isAr ? 'الحساب نشط' : 'Account active',
  }), [isAr]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [modalOpen, setModalOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setUsers(await fetchShareholderUsers()); }
    catch (e) { setError(e instanceof Error ? e.message : t.loadFailed); }
    finally { setLoading(false); }
  }, [t.loadFailed]);

  useEffect(() => {
    if (!getAccessToken() || !isShareholderContextUser()) {
      setError(t.denied);
      setLoading(false);
      return;
    }
    load();
  }, [t.denied, load]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return users;
    return users.filter((u) =>
      u.userName.includes(q) ||
      (u.fullName || '').includes(q) ||
      (u.email || '').includes(q) ||
      (u.phone || '').includes(q),
    );
  }, [users, query]);

  const openCreateModal = () => {
    setEditUserId(null);
    setForm(emptyForm());
    setError('');
    setModalOpen(true);
  };

  const openEditModal = (u) => {
    setEditUserId(u.id);
    setForm({
      userName: u.userName,
      fullName: u.fullName || '',
      email: u.email || '',
      phone: u.phone || '',
      password: '',
      isActive: !!u.isActive,
    });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditUserId(null);
    setForm(emptyForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.userName.trim()) {
      setError(isAr ? 'اسم المستخدم مطلوب' : 'Username is required');
      return;
    }
    if (!editUserId && !form.password.trim()) {
      setError(t.userPassRequired);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (editUserId) {
        await updateShareholderUser(editUserId, {
          userName: form.userName.trim(),
          fullName: form.fullName.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          newPassword: form.password.trim() || undefined,
          isActive: form.isActive,
        });
      } else {
        await createShareholderUser({
          userName: form.userName.trim(),
          password: form.password,
          fullName: form.fullName.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          shareholderPermissionsJson: '{}',
        });
      }
      closeModal();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : (editUserId ? t.updateFailed : t.addFailed));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page users-page">
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editUserId ? t.modalEditTitle : t.modalAddTitle}
        subtitle={editUserId ? t.modalEditSub : t.modalAddSub}
        icon={<Icon name="user" size={18} color="#fff" />}
        size="lg"
        footer={
          <>
            <button type="submit" form="sh-users-modal-form" className="btn btn--primary" disabled={submitting}>
              {submitting ? (editUserId ? t.saving : t.adding) : editUserId ? t.saveChanges : t.save}
            </button>
            <button type="button" className="btn btn--ghost" disabled={submitting} onClick={closeModal}>{t.cancel}</button>
          </>
        }
      >
        <form id="sh-users-modal-form" className="mform" onSubmit={handleSubmit}>
          {error && (
            <div className="mform__field mform__field--full" style={{ marginTop: -6 }}>
              <div className="mform__error" style={{ padding: '10px 12px', background: '#ffebee', borderRadius: 10 }}>{error}</div>
            </div>
          )}
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.username} <span>*</span></label>
              <input className="mform__input" value={form.userName} onChange={(e) => setForm((p) => ({ ...p, userName: e.target.value }))} autoComplete="username" />
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.fullName}</label>
              <input className="mform__input" value={form.fullName} onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.email}</label>
              <input className="mform__input" type="email" dir="ltr" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.phone}</label>
              <input className="mform__input" dir="ltr" placeholder={t.phPhone} value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
          </div>
          <div className="mform__field">
            <label className="mform__label">{editUserId ? t.newPasswordHint : t.password}{!editUserId && <span>*</span>}</label>
            <input className="mform__input" type="password" dir="ltr" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} autoComplete="new-password" />
          </div>
          {editUserId && (
            <div className="mform__field mform__field--full">
              <label className="mform__label" style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
                {t.activeAccount}
              </label>
            </div>
          )}
        </form>
      </Modal>

      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="user" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t.title}</span>
            <span className="list-page-card__sub">{t.subtitle}</span>
          </div>
          <div className="list-page-card__actions">
            <div className="view-toggle">
              <button type="button" className={`view-toggle__btn${viewMode === 'list' ? ' view-toggle__btn--active' : ''}`} onClick={() => setViewMode('list')}><Icon name="list" size={13} /> {t.list}</button>
              <button type="button" className={`view-toggle__btn${viewMode === 'grid' ? ' view-toggle__btn--active' : ''}`} onClick={() => setViewMode('grid')}><Icon name="grid" size={13} /> {t.cards}</button>
            </div>
            <button type="button" className="btn btn--primary" onClick={openCreateModal}><Icon name="plus" size={14} color="#fff" /> {t.addUser}</button>
          </div>
        </header>

        <div className="page__filters"><div className="page__search"><Icon name="search" size={14} color="#90a4ae" /><input className="page__search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t.search} /></div></div>
        {error && !modalOpen && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading ? <div className="list-page-card__loading">{t.loading}</div> : viewMode === 'list' ? (
        <div className="table-card"><table className="data-table"><thead><tr><th>{t.colUser}</th><th>{t.colUsername}</th><th>{t.colStatus}</th><th>{t.colActions}</th></tr></thead><tbody>{filtered.map((u) => <tr key={u.id}><td><div className="users-user-cell"><div className="users-avatar">{(u.fullName || u.userName || '?')[0]}</div><div><div className="users-name">{u.fullName || '—'}</div><div className="users-email">{[u.userName, u.email, u.phone].filter(Boolean).join(' · ') || '—'}</div></div></div></td><td>{u.userName}</td><td><span className={`users-status users-status--${u.isActive ? 'active' : 'inactive'}`}>{u.isActive ? t.active : t.inactive}</span></td><td><button type="button" className="btn btn--ghost" style={{ padding: '6px 12px', fontSize: '0.78rem' }} onClick={() => openEditModal(u)}><Icon name="edit" size={13} /> {t.editUser}</button></td></tr>)}</tbody></table>{filtered.length === 0 && <div className="users-empty">{t.noResults}</div>}</div>
      ) : (
        <div className="users-grid">{filtered.map((u) => <article key={u.id} className="users-card"><div className="users-card__top"><div className="users-avatar users-avatar--lg">{(u.fullName || u.userName || '?')[0]}</div><div><h4>{u.fullName || '—'}</h4><p>{[u.userName, u.email, u.phone].filter(Boolean).join(' · ') || '—'}</p></div><span className={`users-status users-status--${u.isActive ? 'active' : 'inactive'}`}>{u.isActive ? t.active : t.inactive}</span></div><div className="users-card__meta"><div><strong>{t.usernameLabel}</strong> {u.userName}</div></div><div className="users-card__actions"><button type="button" className="btn btn--ghost" onClick={() => openEditModal(u)}><Icon name="edit" size={14} /> {t.editUser}</button></div></article>)}{filtered.length === 0 && <div className="users-empty users-empty--card">{t.noResults}</div>}</div>
      )}
      </div>
    </div>
  );
}
